/**
 * WorkspacePanel
 *
 * A collapsible panel for the idea workspace cockpit. Every stage's content
 * (Concept, Media, Posts, Impact) is present at once; the SMM minimises the
 * panels they aren't working in rather than switching tabs. Collapsed shows
 * just the header bar (title + summary + actions), plus a collapsedBody when
 * the caller passes one, so the layout stays dense without losing the
 * at-a-glance status.
 */

import React, { ReactNode } from "react";
import { cn } from "@/utils/cn";
import { ChevronDownIcon, SpinnerIcon } from "@/components/icons";

interface WorkspacePanelProps {
  title: string;
  /** After the title and a " · ", never truncated: a month after its job's name (A7). The title before it truncates, and
   * the panel is named by both. */
  titleTail?: string;
  icon?: ReactNode;
  /** Short status shown in the header (e.g. "4 photos", "Approvata"). */
  summary?: ReactNode;
  /** Header-right actions (buttons). Hidden while collapsed, unless
   * actionsWhenCollapsed is set. */
  actions?: ReactNode;
  /** Keep the actions in the header while the panel is folded, such as a
   * summary card's Mark paid. */
  actionsWhenCollapsed?: boolean;
  collapsed: boolean;
  /** Absent, the panel never folds: the header is plain, with no chevron or
   * button, and the body always shows. */
  onToggleCollapse?: () => void;
  /** Extra classes on the outer panel (grid placement, etc.). */
  className?: string;
  /** Constrain body height with internal scroll when content is tall. */
  bodyClassName?: string;
  /** DOM id on the outer section — anchor target for #panel links. */
  id?: string;
  /** Render the standard contained skeleton instead of children. */
  loading?: boolean;
  /** Fixed subheader block under the header row — filter tabs, an entity
   * header, or anything else that must stay put while the body scrolls.
   * Hidden while collapsed or loading. */
  subheader?: ReactNode;
  /** A block under the body that stays when the panel folds, such as a
   * summary card's totals (J22). */
  footer?: ReactNode;
  /** Shown in place of the body while the panel is folded, such as a
   * report section's headline figures. */
  collapsedBody?: ReactNode;
  children: ReactNode;
}

export const WorkspacePanel: React.FC<WorkspacePanelProps> = ({
  title,
  titleTail,
  icon,
  summary,
  actions,
  actionsWhenCollapsed = false,
  collapsed: collapsedProp,
  onToggleCollapse,
  className = "",
  bodyClassName = "",
  id,
  loading = false,
  subheader,
  footer,
  collapsedBody,
  children,
}) => {
  // A panel with no toggle cannot be folded, whatever `collapsed` says.
  const collapsed = collapsedProp && onToggleCollapse !== undefined;
  const named = titleTail ? `${title} · ${titleTail}` : title;
  const titleContent = (
    <>
      {onToggleCollapse && (
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-transform ${
            collapsed ? "-rotate-90" : ""
          }`}
        />
      )}
      {icon && <span className="text-gray-400 shrink-0">{icon}</span>}
      {titleTail ? (
        <span className="flex min-w-0 text-sm font-semibold text-gray-900">
          <span className="truncate">{title}</span>
          <span className="shrink-0 whitespace-pre">{` · ${titleTail}`}</span>
        </span>
      ) : (
        <span className="text-sm font-semibold text-gray-900 truncate">
          {title}
        </span>
      )}
      {summary != null && (
        <span className="text-xs text-gray-400 truncate">{summary}</span>
      )}
    </>
  );
  return (
    <section
      id={id}
      className={cn("flex flex-col min-h-0 bg-white rounded-2xl", className)}
    >
      <header className="shrink-0">
        <div className="flex items-center gap-3 px-4 h-12">
        {onToggleCollapse ? (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-expanded={!collapsed}
            aria-label={collapsed ? `Expand ${named}` : `Minimise ${named}`}
            className="flex items-center gap-2 min-w-0 flex-1 text-left group"
          >
            {titleContent}
          </button>
        ) : (
          <div className="flex items-center gap-2 min-w-0 flex-1 text-left">
            {titleContent}
          </div>
        )}
          {(!collapsed || actionsWhenCollapsed) && actions && (
            <div className="flex items-center gap-2 shrink-0">{actions}</div>
          )}
        </div>
        {/* No overflow clipping here — subheader content may open dropdowns;
            scrollable filter bars bring their own overflow-x (PanelFilterTabs). */}
        {!collapsed && !loading && subheader && (
          <div className="px-4 pb-2.5 -mt-1">{subheader}</div>
        )}
      </header>
      {!collapsed && (
        <div
          className={cn(
            "flex-1 min-h-0 p-4",
            loading ? "overflow-hidden" : "overflow-y-auto",
            bodyClassName
          )}
        >
          {loading ? (
            <div
              className="flex items-center justify-center py-8"
              data-testid="panel-loading"
            >
              <SpinnerIcon className="w-5 h-5 text-gray-400" />
            </div>
          ) : (
            children
          )}
        </div>
      )}
      {collapsed && collapsedBody != null && (
        <div className="shrink-0 px-4 pb-4 -mt-1">{collapsedBody}</div>
      )}
      {footer ? <div className="shrink-0">{footer}</div> : null}
    </section>
  );
};
